import React, { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { ModalPageView } from '@cotera/client/app/components/app';
import { Text } from '@cotera/client/app/components/ui';
import { Separator } from '@cotera/client/v0/components/ui/separator';
import type { DeveloperDetails } from '../framework/types';

const CopyableDetail: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (): void => {
    if (typeof navigator === 'undefined' || navigator.clipboard === undefined) {
      return;
    }
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
      <button
        type="button"
        onClick={handleCopy}
        title="Copy"
        className="group inline-flex min-w-0 items-center gap-1.5 font-mono text-xs text-slate-700 hover:text-foreground"
      >
        <span className="truncate">{value}</span>
        {copied ? (
          <Check className="h-3 w-3 shrink-0 text-emerald-600" />
        ) : (
          <Copy className="h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
        )}
      </button>
    </div>
  );
};

export const DeveloperDetailsModal: React.FC<{
  sections: DeveloperDetails[];
}> = ({ sections }) => {
  const subtitle =
    sections.length === 1
      ? `Identifiers for ${sections[0]!.title.toLowerCase()}`
      : 'Identifiers for the current page';

  return (
    <ModalPageView
      title="Developer details"
      subtitle={subtitle}
      className="px-4 py-4"
    >
      <div className="flex flex-col gap-4">
        {sections.map((section, index) => (
          <div key={`${section.title}-${index}`}>
            {sections.length > 1 ? (
              <Text.Caption className="mb-2 px-1">{section.title}</Text.Caption>
            ) : null}
            <div className="rounded-lg border border-border/60 bg-background px-3 py-1">
              {section.entries.map((entry) => (
                <CopyableDetail
                  key={`${entry.key}-${entry.value}`}
                  label={entry.key}
                  value={entry.value}
                />
              ))}
            </div>
            {index < sections.length - 1 ? (
              <Separator className="mt-4" />
            ) : null}
          </div>
        ))}
      </div>
    </ModalPageView>
  );
};
